import { DataTableColumnHeader } from "@pma/components/common/data-table/data-table-column-header"
import { Table, TableHead, TableHeader, TableRow } from "@pma/components/common/table"
import { useCustomColumns } from "@pma/stores/tasks-store"
import { DictType } from "@pma/types"
import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table"
import * as React from "react"
import TaskEditor from "../task-editor"
import { tasksColumns } from "../tasks-table-columns"
import useTranslator from "@pma/hooks/use-translator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@pma/components/common/tooltip"
import { format, formatDistance } from "date-fns"
import { he } from "date-fns/locale"
import ExpandTask from "../expand-task"
import SubTaskMenu from "../sub-task-menu"
import { Pin, PinOff } from "lucide-react"
import TBody from "@pma/components/common/data-table/table-body"
import { useParams } from "react-router-dom"
import { DataTablePagination } from "@pma/components/common/data-table/data-table-pagination"
import { GroupedTasksContext } from "./context/GroupedTasksProvider"
import { taskFilterFn } from "@pma/utils/helpers"
import { useQuery } from "@tanstack/react-query"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"

interface ITableSectionProps {
  tasks: Array<DictType>
  projectId: string
  group_id: null | number | undefined
  isLoading: boolean
  searchKey?: string
  setGroupEntryCounts: any
  groupEntryCounts: any
  highlightedTaskId: number | null
  taskCounter: number
}

const TableSection: React.FC<ITableSectionProps> = ({
  tasks,
  projectId,
  group_id,
  isLoading,
  searchKey,
  taskCounter,
  setGroupEntryCounts,
  groupEntryCounts,
  highlightedTaskId,
}) => {
  const tableRef = React.useRef<any>(null)
  const { locale } = useTranslator()
  const { customColumns } = useCustomColumns()
  const paramProjectId = useParams()?.projectId
  const bottomRef = React.useRef<HTMLDivElement>(null)
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const {
    paginationData,
    setPagination,
    setSelectedTasks,
    selectedTasks,
    showColumns,
    groupedTasks,
  } = React.useContext(GroupedTasksContext)
  const [showLoader, setShowLoader] = React.useState(false)
  const [localTasks, setLocalTasks] = React.useState<DictType[]>(tasks)
  const [sorting, setSorting] = React.useState<SortingState>([])

  React.useEffect(() => {
    setLocalTasks(tasks)
  }, [tasks])

  React.useEffect(() => {
    setShowLoader(isLoading)
  }, [isLoading])

  React.useEffect(() => {
    const group = groupedTasks.find((g) => g.id === group_id)
    if (group && group._tasks) setLocalTasks(group._tasks)
  }, [groupedTasks, group_id])

  const dynamicColumns: ColumnDef<any>[] = React.useMemo(() => {
    return customColumns?.map((cColumn: DictType) => ({
      accessorKey: cColumn?.name,
      header: ({ column }: any) => (
        <DataTableColumnHeader column={column} title={column?.id} className='' />
      ),
      cell: ({ row }: DictType) => {
        const customColumns = row.original?.custom_columns
        const customValue = customColumns?.filter(
          (column: DictType) => column?.name === cColumn?.name,
        )[0]

        return (
          <TaskEditor
            id={row.original?.id}
            title={cColumn?.name}
            value={customValue?.value}
            customId={customValue?.column_id}
            columnType={customValue?.type || cColumn?.type}
            selectorList={customValue?.list || cColumn?.list}
            customFile={customValue?._custom_file}
          >
            {customValue?.value || "---"}
          </TaskEditor>
        )
      },
      sortingFn: (rowA, rowB) => {
        const customValueA = rowA.original.custom_columns.find(
          (column: DictType) => column?.name === cColumn?.name,
        )?.value
        const customValueB = rowB.original.custom_columns.find(
          (column: DictType) => column?.name === cColumn?.name,
        )?.value

        if (typeof customValueA === "number" && typeof customValueB === "number") {
          return customValueA - customValueB
        } else if (typeof customValueA === "string" && typeof customValueB === "string") {
          return customValueA.localeCompare(customValueB)
        } else {
          return 0
        }
      },
      enableSorting: true,
    }))
  }, [customColumns])

  const columns = React.useMemo(() => {
    return [
      ...tasksColumns,
      ...dynamicColumns,
      {
        accessorKey: "created_at",
        enableSorting: true,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Created At' className='' />
        ),
        cell: ({ row }: any) => {
          const isHebrew = locale === "he"
          const task = row?.original as DictType
          const createdAt = task?.created_at
          return createdAt == undefined ? (
            <> </>
          ) : (
            <Tooltip>
              <TooltipTrigger>
                <small>
                  {formatDistance(createdAt, new Date(), {
                    locale: isHebrew ? he : undefined,
                    addSuffix: true,
                  })}
                </small>{" "}
              </TooltipTrigger>
              <TooltipContent>
                <div className='w-fit text-xs'>{format(createdAt, "PPP")}</div>
              </TooltipContent>
            </Tooltip>
          )
        },
        sortingFn: (rowA, rowB) => {
          const taskOneCreatedAt = rowA.original.created_at || ""
          const taskTwoCreatedAt = rowB.original.created_at || ""
          return taskOneCreatedAt < taskTwoCreatedAt
            ? -1
            : taskOneCreatedAt > taskTwoCreatedAt
              ? 1
              : 0
        },
      },
      {
        id: "actions",
        header: ({ column }) => <DataTableColumnHeader column={column} title='Actions' />,
        cell: ({ row }) => (
          <div className='flex items-center justify-end text-red-500'>
            <ExpandTask task={row.original} row={row.original.parent_tasks_id} />
            <SubTaskMenu task={row.original} group_id={group_id} />
          </div>
        ),
      },
    ]
  }, [tasksColumns, dynamicColumns])

  const table = useReactTable({
    data: tasks,
    state: {
      columnVisibility,
      globalFilter: "",
      sorting,
    },
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: false,
    manualExpanding: false,
    debugTable: false,
    getSubRows: (row) => row._subtasks,
    globalFilterFn: taskFilterFn,
    onSortingChange: setSorting,
    manualSorting: true,
    defaultColumn: {
      size: 200, //starting column size
      minSize: 50, //enforced during column resizing
      maxSize: 500, //enforced during column resizing
    },
  })

  React.useEffect(() => {
    const { flatRows } = table.getGroupedSelectedRowModel() // call function once
    const arr =
      flatRows.length == 0
        ? []
        : flatRows.map((val) => ({
            taskId: val.original.id,
            priorityId: val.original.priority_id,
            responsible_id: val.original.responsible_id,
            project_id: val.original.project_id,
            group_id: val.original.tasks_group_id,
          }))

    if (tasks.length > 0) {
      const idArr: number[] = []
      tasks.forEach((val) => {
        if (val._subtasks.length > 0) {
          idArr.push(val.id)
          idArr.push(...val._subtasks.map((val2: any) => val2.id))
        } else {
          idArr.push(val.id)
        }
      })

      const newArr = selectedTasks.filter((val) => !idArr.includes(val.taskId))
      setSelectedTasks([...newArr, ...arr])
    }
  }, [table.getGroupedSelectedRowModel(), tasks, setSelectedTasks])

  const getCommonPinningStyles = (column: Column<DictType>): React.CSSProperties => {
    const isPinned = column.getIsPinned()
    return {
      boxShadow: isPinned ? "-4px 0 4px -4px gray inset" : undefined,
      left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
      opacity: isPinned ? 0.95 : 1,
      position: isPinned ? "sticky" : "relative",
      width: column.getSize(),
      zIndex: isPinned ? 1 : 0,
      background: isPinned ? "white" : undefined,
    }
  }

  const columnSizingHandler = (
    thElem: HTMLTableCellElement | null,
    table: any,
    column: Column<any>,
  ) => {
    if (!thElem) return
    const currentWidth = thElem.getBoundingClientRect().width
    if (table.getState().columnSizing[column.id] === currentWidth) return

    table.setColumnSizing((prevSizes: any) => ({
      ...prevSizes,
      [column.id]: currentWidth,
    }))
  }

  const handleHeaderPin = (pin: any, pinStatus: boolean | string = false) => {
    const pinnedArr = localStorage.getItem("pinned")
      ? JSON.parse(localStorage?.getItem("pinned") || "")
      : []

    if (pinnedArr && pinnedArr.length > 0) {
      const currentProject = pinnedArr.find((val: any) => val.projectId === projectId)
      if (currentProject) {
        if (pinStatus === false) {
          const index = pinnedArr.indexOf(currentProject)
          const pinArr = currentProject.pinned.filter((val: string) => val !== pin.id)
          currentProject["pinned"] = pinArr
          pinnedArr[index] = currentProject

          pin.column.pin(false)
          localStorage.setItem("pinned", JSON.stringify(pinnedArr))
        } else {
          const index = pinnedArr.indexOf(currentProject)
          const pinArr = currentProject.pinned
          pinArr.push(pin.id)
          currentProject["pinned"] = pinArr
          pinnedArr[index] = currentProject

          pin.column.pin(pinStatus)
          localStorage.setItem("pinned", JSON.stringify(pinnedArr))
        }
      } else {
        if (pinStatus === "left") {
          const pinArr = []
          pinArr.push(pin.id)
          pinnedArr.push({
            projectId: projectId,
            pinned: pinArr,
          })

          pin.column.pin(pinStatus)
          localStorage.setItem("pinned", JSON.stringify(pinnedArr))
        }
      }
    } else {
      if (pinStatus === false) {
        pin.column.pin(false)
      } else {
        const pinArr = []
        pinArr.push(pin.id)
        pinnedArr.push({
          projectId: projectId,
          pinned: pinArr,
        })

        pin.column.pin(pinStatus)
        localStorage.setItem("pinned", JSON.stringify(pinnedArr))
      }
    }
  }

  useQuery({
    queryKey: ["clear-selected-group-tasks"],
    queryFn: () => {
      table.resetRowSelection()
      return false
    },
    refetchOnMount: false,
  })

  React.useEffect(() => {
    if (selectedTasks.length === 0) {
      table.resetRowSelection()
    }
  }, [selectedTasks, table])

  React.useEffect(() => {
    const { rows } = table.getFilteredRowModel()
    const existingGroup = groupEntryCounts.find((val: any) => val.group_id === group_id)
    if (existingGroup) {
      const index = groupEntryCounts.indexOf(existingGroup)
      groupEntryCounts[index]["count"] =
        rows.length < taskCounter
          ? taskCounter - 1
          : rows.length == taskCounter
            ? taskCounter
            : taskCounter + 1
    } else {
      setGroupEntryCounts((prev: any) => {
        return [...prev, { group_id, count: taskCounter }]
      })
    }
  }, [table.getFilteredRowModel()])

  React.useEffect(() => {
    const col = table.getAllColumns()
    showColumns.forEach((scl) => {
      col.forEach((cl) => {
        if (scl.id === cl.id) {
          cl.toggleVisibility(scl.show)
        }
      })
    })
  }, [showColumns, table])

  const tasksIds = React.useMemo(() => {
    const ids: string[] = []
    if (localTasks.length === 0) {
      ids.push(`empty_${group_id}`)
    } else {
      localTasks.forEach((task) => {
        ids.push(`task_${task.id}_${group_id}`)
        if (task._subtasks && task._subtasks.length > 0) {
          task._subtasks.forEach((subtask: any) => {
            ids.push(`subtask_${task.id}_${subtask.id}_${group_id}`)
          })
        }
      })
    }
    return ids
  }, [localTasks, group_id])

  return (
    <div className='overflow-x-auto'>
      <Table ref={tableRef}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => {
            return (
              <TableRow
                key={headerGroup.id}
                className={`${"sticky top-0 z-[15] bg-white/30 backdrop-blur-md"} `}
              >
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{ ...getCommonPinningStyles(header.column) }}
                      ref={(thElem) => columnSizingHandler(thElem, table, header.column)}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className='flex'>
                        <span className='flex cursor-pointer items-center space-x-2'>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}{" "}
                        </span>
                        <div className='flex w-full flex-1 items-center'>
                          {header.column.getIsPinned() ? (
                            <PinOff
                              onClick={() => handleHeaderPin(header)}
                              className='m-1 h-3.5 w-3.5 text-muted-foreground/70'
                            />
                          ) : (
                            <Pin
                              onClick={() => handleHeaderPin(header, "left")}
                              className='m-1 h-3.5 w-3.5 text-muted-foreground/70'
                            />
                          )}
                        </div>
                      </div>
                    </TableHead>
                  )
                })}
              </TableRow>
            )
          })}
        </TableHeader>
        <SortableContext items={tasksIds} strategy={verticalListSortingStrategy}>
          <TBody
            table={table}
            showLoader={showLoader}
            projectId={paramProjectId}
            group_id={group_id}
            columns={columns}
            getCommonPinningStyles={getCommonPinningStyles}
            context={"tasks"}
            highlightedTaskId={highlightedTaskId}
          />
        </SortableContext>
      </Table>
      {group_id == -1 && (
        <>
          {" "}
          <div ref={bottomRef} className='h-1' />
          {paginationData && (
            <DataTablePagination
              data={tasks}
              paginationData={paginationData}
              setPagination={setPagination}
            />
          )}{" "}
        </>
      )}
    </div>
  )
}

export default TableSection
